import React, { useState } from 'react';
import { FileText, Download } from 'lucide-react';
import { Button, Card, FileUploadZone, ProgressBar, Alert, Badge, PDFPreview, ToolActionButtons, NoteCard } from '../shared';
import { PDFDocument, rgb, StandardFonts } from 'pdf-lib';
import { saveAs } from 'file-saver';
import { toast } from 'react-toastify';
import mammoth from 'mammoth';
import '../../styles/design-system.css';

const WORDtoPDF = () => {
    const [file, setFile] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [progress, setProgress] = useState(0);
    const [preview, setPreview] = useState(null);
    const [previewBlob, setPreviewBlob] = useState(null);
    const [previewFilename, setPreviewFilename] = useState('');

    const handleFileSelected = async (files) => {
        if (files && files.length > 0) {
            const selectedFile = files[0];
            setFile(selectedFile);
            setPreview(null);

            // Generate preview
            try {
                const arrayBuffer = await selectedFile.arrayBuffer();
                const result = await mammoth.convertToHtml({ arrayBuffer });
                setPreview(result.value);
                toast.success('DOCX loaded successfully');
            } catch (error) {
                toast.error(`Failed to load DOCX: ${error.message}`);
            }
        }
    };

    const convertToPDF = async () => {
        if (!file) return;

        try {
            setIsProcessing(true);
            setProgress(10);

            // Convert DOCX to HTML
            const arrayBuffer = await file.arrayBuffer();
            const result = await mammoth.convertToHtml({ arrayBuffer });
            const htmlContent = result.value;

            setProgress(40);

            // Create PDF
            const pdfDoc = await PDFDocument.create();
            const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
            const boldFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

            setProgress(60);

            // Parse HTML and extract text
            const parser = new DOMParser();
            const doc = parser.parseFromString(htmlContent, 'text/html');
            const textContent = doc.body.textContent || doc.body.innerText || '';

            // Create pages with text
            const lines = textContent.split('\n').filter(line => line.trim());
            let page = pdfDoc.addPage([595, 842]); // A4
            let yPosition = 792;
            const fontSize = 12;
            const lineHeight = fontSize + 4;
            const margin = 50;
            const maxWidth = 495;

            for (const line of lines) {
                if (yPosition < margin + lineHeight) {
                    page = pdfDoc.addPage([595, 842]);
                    yPosition = 792;
                }

                // Word wrap
                const words = line.split(' ');
                let currentLine = '';

                for (const word of words) {
                    const testLine = currentLine + (currentLine ? ' ' : '') + word;
                    const textWidth = font.widthOfTextAtSize(testLine, fontSize);

                    if (textWidth > maxWidth && currentLine) {
                        page.drawText(currentLine, {
                            x: margin,
                            y: yPosition,
                            size: fontSize,
                            font: font,
                            color: rgb(0, 0, 0),
                        });
                        yPosition -= lineHeight;
                        currentLine = word;

                        if (yPosition < margin + lineHeight) {
                            page = pdfDoc.addPage([595, 842]);
                            yPosition = 792;
                        }
                    } else {
                        currentLine = testLine;
                    }
                }

                if (currentLine) {
                    page.drawText(currentLine, {
                        x: margin,
                        y: yPosition,
                        size: fontSize,
                        font: font,
                        color: rgb(0, 0, 0),
                    });
                    yPosition -= lineHeight;
                }
            }

            setProgress(90);

            const pdfBytes = await pdfDoc.save();
            const blob = new Blob([pdfBytes], { type: 'application/pdf' });

            setProgress(100);

            const timestamp = new Date().toISOString().slice(0, 10);
            const fileName = file.name.replace(/\.docx?$/i, '');
            const filename = `${fileName}.pdf`;

            // Set preview
            setPreviewBlob(blob);
            setPreviewFilename(filename);

            toast.success('DOCX converted! Opening preview...');

            setTimeout(() => {
                setProgress(0);
            }, 1000);

        } catch (error) {
            console.error('Conversion error:', error);
            toast.error(`Failed to convert: ${error.message}`);
        } finally {
            setIsProcessing(false);
        }
    };

    return (
        <div>
            {/* Action Buttons */}
            <ToolActionButtons
                onAdd={() => {
                    document.querySelector('input[type="file"]')?.click();
                }}
                notificationCount={file ? 1 : 0}
            />

            {/* Header */}
            <div className="tool-header">
                <h1 className="tool-title">
                    <FileText size={36} style={{ display: 'inline', marginRight: 'var(--spacing-3)', color: 'var(--color-accent)' }} />
                    WORD to PDF
                </h1>
                <p className="tool-description">
                    Convert Microsoft Word documents (DOCX) to PDF format while preserving formatting.
                </p>
            </div>

            {/* File Upload */}
            {!file ? (
                <FileUploadZone
                    onFilesSelected={handleFileSelected}
                    acceptedTypes={['.docx', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document']}
                    multiple={false}
                    title="Drop DOCX file here"
                    description="or click to browse"
                />
            ) : (
                <Alert variant="success" title="DOCX Loaded">
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)' }}>
                            <FileText size={16} />
                            <span>{file.name}</span>
                        </div>
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                                setFile(null);
                                setPreview(null);
                            }}
                        >
                            Change File
                        </Button>
                    </div>
                </Alert>
            )}

            {file && preview && (
                <>
                    {/* Preview */}
                    <div style={{ marginTop: 'var(--spacing-6)' }}>
                        <Card>
                            <h3 style={{
                                fontSize: 'var(--text-lg)',
                                fontWeight: 'var(--font-bold)',
                                marginBottom: 'var(--spacing-4)'
                            }}>
                                Document Preview
                            </h3>
                            <div
                                style={{
                                    maxHeight: '400px',
                                    overflowY: 'auto',
                                    padding: 'var(--spacing-4)',
                                    backgroundColor: 'var(--color-surface-elevated)',
                                    borderRadius: 'var(--radius-md)',
                                    fontSize: 'var(--text-sm)',
                                    lineHeight: 1.6
                                }}
                                dangerouslySetInnerHTML={{ __html: preview }}
                            />
                        </Card>
                    </div>

                    {/* Progress Bar */}
                    {isProcessing && (
                        <div style={{ marginTop: 'var(--spacing-6)' }}>
                            <ProgressBar value={progress} striped showLabel />
                        </div>
                    )}

                    {/* Convert Button */}
                    <div style={{ marginTop: 'var(--spacing-6)' }}>
                        <Button
                            variant="primary"
                            size="lg"
                            onClick={convertToPDF}
                            disabled={isProcessing}
                            loading={isProcessing}
                            fullWidth
                        >
                            <Download size={20} />
                            Convert to PDF
                        </Button>
                    </div>

                    {/* Info */}
                    <NoteCard variant="info">
                        <strong>Note:</strong> Complex formatting, images, and tables may not be perfectly preserved. For best results, use simple text documents.
                    </NoteCard>
                </>
            )}

            {/* PDF Preview Modal */}
            {previewBlob && (
                <PDFPreview
                    pdfBlob={previewBlob}
                    filename={previewFilename}
                    onClose={() => setPreviewBlob(null)}
                    onDownload={() => {
                        if (previewBlob && previewFilename) {
                            saveAs(previewBlob, previewFilename);
                            toast.success('Download started!');
                            setPreviewBlob(null);
                        }
                    }}
                />
            )}
        </div>
    );
};

export default WORDtoPDF;
